import { FC, useEffect } from 'react';
import classNames from 'classnames';

import { AppModalProps } from '@/types/modal.type';
import { ModalView } from '@/Components/ModalView';
import { Button } from '@/Components/Button';
import { suggestionImgMap } from '@/Components/FlowSuggestionRowItem/FlowSuggestionRowItem.constant';

export type ImportTemplateModalProps = AppModalProps<{
    workspaceName: string;
    pendingTemplates: {
        slug: string;
        name: string;
        description: string;
    }[];
    onConfirm: () => void;
    onCancel: () => void;
}>;

export const ImportTemplateModal: FC<ImportTemplateModalProps> = ({
    onClose,
    payload,
}) => {
    const { workspaceName, pendingTemplates, onCancel } = payload;

    const onConfirm = () => {
        payload?.onConfirm();

        onClose();
    };

    const onCancelClick = () => {
        onClose();
    };

    useEffect(() => {
        return () => {
            onCancel();
        };
    }, []);

    return (
        <ModalView
            maxWidth="sm"
            onClose={onClose}
            onSubmit={onClose}
            contentContainerClassName="py-6 px-8 text-brand-ocean flex flex-col justify-center items-center">
            <div className="w-full !font-normal text-body-text-15 text-left text-brand-ocean mb-2">
                Are you sure you want to import the following{' '}
                <span className="font-bold">
                    {pendingTemplates.length > 1
                        ? `${pendingTemplates.length} `
                        : ''}
                    template
                    {pendingTemplates.length > 1 ? 's' : ''}
                </span>{' '}
                into <span className="font-bold">{workspaceName}</span>{' '}
                workspace?
            </div>
            <div className="my-3 w-full flex flex-col items-start justify-center border border-gray-20/70 rounded-md">
                {pendingTemplates.map((template, index) => (
                    <div
                        key={template.slug}
                        className={classNames(
                            'w-full flex items-center justify-center py-3 px-3',
                            {
                                'border-b border-gray-20/70':
                                    index < pendingTemplates.length - 1,
                            },
                        )}>
                        <div className="h-10 min-h-10 w-10 min-w-10 bg-gray-20 rounded-md overflow-hidden aspect-square">
                            <img
                                src={
                                    // @ts-expect-error
                                    suggestionImgMap[template.slug] ||
                                    suggestionImgMap[
                                        'example_suggestion_homepage'
                                    ]
                                }
                                alt={template.name}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="ml-2">
                            <div className="text-small-text-13 text-brand-ocean">
                                {template.name}
                            </div>
                            <div className="text-small-text-13 text-gray-80">
                                {template.description}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-2 w-full flex items-center justify-center space-x-3">
                <Button
                    className="w-full h-9"
                    variant="default"
                    onClick={onCancelClick}>
                    Cancel
                </Button>
                <Button
                    className="w-full h-9"
                    variant="primary"
                    onClick={onConfirm}>
                    Continue & Import
                </Button>
            </div>
        </ModalView>
    );
};
